/**
 * JSON-RPC transport. Everything goes to /rpc on this origin (the Pages
 * Function proxies it and rewrites the session cookie to belong here).
 */

export class RpcError extends Error {
  // Plain fields rather than TS parameter properties: the test runner strips
  // types rather than compiling, and parameter properties need a real compile.
  code: number | string
  httpStatus: number
  retryAfter: number | undefined

  constructor(code: number | string, message: string, httpStatus: number, retryAfter?: number) {
    super(message)
    this.name = 'RpcError'
    this.code = code
    this.httpStatus = httpStatus
    this.retryAfter = retryAfter
  }
  get isAuth(): boolean {
    return this.httpStatus === 401 || this.code === 401 || /not logged in|unauthor/i.test(this.message)
  }
}

let seq = 0

interface RpcOptions {
  /** Bearer for log_data.* calls. */
  token?: string | null
  signal?: AbortSignal
  /** Sees the raw response before it is parsed — for the proxy's diagnostic headers. */
  onResponse?: (res: Response) => void
}

/** One JSON-RPC round trip. Throws RpcError on `error`, returns `result`. */
export async function rpc<T = unknown>(
  method: string,
  params: Record<string, unknown> = {},
  opts: RpcOptions = {},
): Promise<T> {
  // Same headers the reference client sends; the upstream is known to work
  // with exactly these.
  const headers: Record<string, string> = {
    accept: 'application/json, text/plain, */*',
    'content-type': 'application/json;charset=UTF-8',
  }
  if (opts.token) headers.authorization = `Bearer ${opts.token}`

  const res = await fetch('/rpc', {
    method: 'POST',
    headers,
    // Without this the HttpOnly session cookie is never attached.
    credentials: 'include',
    body: JSON.stringify({ id: ++seq, method, params }),
    signal: opts.signal,
  })

  opts.onResponse?.(res)
  const retryAfter = Number(res.headers.get('retry-after')) || undefined
  let body: any = null
  try {
    body = await res.json()
  } catch {
    throw new RpcError(res.status, `${method}: non-JSON reply (HTTP ${res.status})`, res.status, retryAfter)
  }

  // HTTP 200 does not mean success — always check `error` first.
  if (body && body.error) {
    const e = body.error
    const detail = e.msg ?? e.message ?? 'rpc error'
    const where = res.ok ? '' : ` (HTTP ${res.status})`
    throw new RpcError(e.code ?? res.status, `${method}: ${detail}${where}`, res.status, retryAfter)
  }
  if (!res.ok) {
    throw new RpcError(res.status, `${method}: HTTP ${res.status} ${res.statusText}`.trim(), res.status, retryAfter)
  }
  return body.result as T
}

// --- bearer handling -------------------------------------------------------

export interface AdminInfo {
  user_id: number
  privilege: number
  token: string
}

let token: string | null = null
let refreshing: Promise<AdminInfo> | null = null

export function currentToken(): string | null {
  return token
}

export function clearToken(): void {
  token = null
  refreshing = null
}

/**
 * `users.admin` doubles as "am I still logged in?" and as the bearer source.
 * Concurrent callers share one in-flight refresh, so a burst of parallel
 * log_data calls triggers one refresh rather than twenty.
 */
export function refreshAdmin(): Promise<AdminInfo> {
  if (refreshing) return refreshing
  refreshing = rpc<AdminInfo>('users.admin', { session_token: '' })
    .then((info) => {
      token = info?.token ?? null
      return info
    })
    .finally(() => {
      refreshing = null
    })
  return refreshing
}

/** An authenticated call: attaches the bearer, refreshes once on 401, retries once. */
export async function protectedRpc<T = unknown>(
  method: string,
  params: Record<string, unknown> = {},
  opts: Omit<RpcOptions, 'token'> = {},
): Promise<T> {
  if (!token) await refreshAdmin()
  try {
    return await rpc<T>(method, params, { ...opts, token })
  } catch (err) {
    if (err instanceof RpcError && err.isAuth) {
      token = null
      await refreshAdmin()
      return await rpc<T>(method, params, { ...opts, token })
    }
    throw err
  }
}
